import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { AdminRoute, AnalystRoute, EditorRoute, ProtectedRoute } from './guards';

/**
 * All routes are lazy-loaded so each page becomes its own chunk.
 * The Suspense boundary lives inside AppLayout around the <Outlet />.
 */

// Dashboards
const DashboardOverviewPage = lazy(() => import('@/pages/dashboard/DashboardOverviewPage'));
const BrandPerformancePage = lazy(() => import('@/pages/dashboard/BrandPerformancePage'));
const BrandDeepDivePage = lazy(() => import('@/pages/dashboard/BrandDeepDivePage'));
const ContentMixPage = lazy(() => import('@/pages/dashboard/ContentMixPage'));
const OfferStrategyPage = lazy(() => import('@/pages/dashboard/OfferStrategyPage'));
const StrategicIntentPage = lazy(() => import('@/pages/dashboard/StrategicIntentPage'));
const SendTimingPage = lazy(() => import('@/pages/dashboard/SendTimingPage'));
const PersonaPerformancePage = lazy(() => import('@/pages/dashboard/PersonaPerformancePage'));
const MonthlyCadencePage = lazy(() => import('@/pages/dashboard/MonthlyCadencePage'));
const HourlyCadencePage = lazy(() => import('@/pages/dashboard/HourlyCadencePage'));

// Core pages
const ComparisonPage = lazy(() => import('@/pages/ComparisonPage'));
const MessageExplorerPage = lazy(() => import('@/pages/MessageExplorerPage'));
const EmailDetailPage = lazy(() => import('@/pages/EmailDetailPage'));
const TextMessageDetailPage = lazy(() => import('@/pages/TextMessageDetailPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Public (pre-auth) — invite welcome-email flows
const WelcomeSetPasswordPage = lazy(() => import('@/pages/WelcomeSetPasswordPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));

// AI tools
const AiSuggestionsPage = lazy(() => import('@/pages/ai/AiSuggestionsPage'));
const AiPromptingPage = lazy(() => import('@/pages/ai/AiPromptingPage'));

// Notifications + settings
const NotificationCenterPage = lazy(() => import('@/pages/notifications/NotificationCenterPage'));
const NotificationSettingsPage = lazy(
  () => import('@/pages/notifications/NotificationSettingsPage'),
);
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/settings/ProfilePage'));

// Admin (crm:admin only)
const BrandsAdminPage = lazy(() => import('@/pages/admin/BrandsAdminPage'));
const BusinessUnitsAdminPage = lazy(() => import('@/pages/admin/BusinessUnitsAdminPage'));
const PersonasAdminPage = lazy(() => import('@/pages/admin/PersonasAdminPage'));
const PersonaAssignmentsPage = lazy(() => import('@/pages/admin/PersonaAssignmentsPage'));
const TeamsAdminPage = lazy(() => import('@/pages/admin/TeamsAdminPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));
const AiModelsAdminPage = lazy(() => import('@/pages/admin/AiModelsAdminPage'));
const SenderPhoneNumbersAdminPage = lazy(() => import('@/pages/admin/SenderPhoneNumbersAdminPage'));
const ReclassifyAdminPage = lazy(() => import('@/pages/admin/ReclassifyAdminPage'));
const CampaignTagsAdminPage = lazy(() =>
  import('@/pages/admin/TagManagerPage').then((m) => ({ default: m.CampaignTagsAdminPage })),
);
const OfferTypeTagsAdminPage = lazy(() =>
  import('@/pages/admin/TagManagerPage').then((m) => ({ default: m.OfferTypeTagsAdminPage })),
);
const FunnelTagsAdminPage = lazy(() =>
  import('@/pages/admin/TagManagerPage').then((m) => ({ default: m.FunnelTagsAdminPage })),
);

function admin(Page: LazyExoticComponent<ComponentType>) {
  return (
    <AdminRoute>
      <Page />
    </AdminRoute>
  );
}

function editor(Page: LazyExoticComponent<ComponentType>) {
  return (
    <EditorRoute>
      <Page />
    </EditorRoute>
  );
}

function analyst(Page: LazyExoticComponent<ComponentType>) {
  return (
    <AnalystRoute>
      <Page />
    </AnalystRoute>
  );
}

export const router = createBrowserRouter([
  // Public — outside the auth-protected tree; the invite token is the credential.
  {
    path: '/welcome/set-password',
    element: (
      <Suspense fallback={<LoadingSpinner fullPage label="Loading..." />}>
        <WelcomeSetPasswordPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  // Public — drives Okta invite links through the Auth0 SDK's own login
  // flow (see LoginPage) instead of a hand-built /authorize URL.
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingSpinner fullPage label="Loading..." />}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Dashboards
      { path: 'dashboard', element: <DashboardOverviewPage /> },
      { path: 'dashboard/brand-performance', element: <BrandPerformancePage /> },
      { path: 'dashboard/brand-deep-dive', element: <BrandDeepDivePage /> },
      { path: 'dashboard/content-mix', element: <ContentMixPage /> },
      { path: 'dashboard/offer-strategy', element: <OfferStrategyPage /> },
      { path: 'dashboard/strategic-intent', element: <StrategicIntentPage /> },
      { path: 'dashboard/send-timing', element: <SendTimingPage /> },
      { path: 'dashboard/persona-performance', element: <PersonaPerformancePage /> },
      { path: 'dashboard/monthly-cadence', element: <MonthlyCadencePage /> },
      { path: 'dashboard/hourly-cadence', element: <HourlyCadencePage /> },

      // Comparison + messages (email + SMS/MMS)
      { path: 'comparison', element: <ComparisonPage /> },
      { path: 'message-explorer', element: <MessageExplorerPage /> },
      // Legacy explorer URLs kept for bookmarks
      { path: 'emails', element: <Navigate to="/message-explorer" replace /> },
      { path: 'email-explorer', element: <Navigate to="/message-explorer" replace /> },
      { path: 'emails/:id', element: <EmailDetailPage /> },
      { path: 'texts/:id', element: <TextMessageDetailPage /> },

      // AI tools
      {
        path: 'ai/suggestions',
        element: (
          <AnalystRoute>
            <AiSuggestionsPage />
          </AnalystRoute>
        ),
      },
      {
        path: 'ai/prompting',
        element: (
          <AnalystRoute>
            <AiPromptingPage />
          </AnalystRoute>
        ),
      },

      // Notifications
      { path: 'notifications', element: <NotificationCenterPage /> },
      { path: 'notifications/settings', element: <NotificationSettingsPage /> },

      // Settings
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/profile', element: <ProfilePage /> },

      // Admin — Iris employees only
      { path: 'admin/brands', element: admin(BrandsAdminPage) },
      { path: 'admin/business-units', element: admin(BusinessUnitsAdminPage) },
      // Personas + tags + AI models — editors/analysts get a read-only view, admins can edit
      { path: 'admin/personas', element: analyst(PersonasAdminPage) },
      { path: 'admin/persona-assignments', element: analyst(PersonaAssignmentsPage) },
      // Teams + users — editors get a delegated view, admins get full management
      { path: 'admin/teams', element: editor(TeamsAdminPage) },
      { path: 'admin/users', element: editor(UserManagementPage) },
      // Legacy URL kept for bookmarks
      { path: 'admin/client-groups', element: <Navigate to="/admin/teams" replace /> },
      { path: 'admin/campaign-tags', element: analyst(CampaignTagsAdminPage) },
      { path: 'admin/offer-type-tags', element: analyst(OfferTypeTagsAdminPage) },
      { path: 'admin/funnel-tags', element: analyst(FunnelTagsAdminPage) },
      { path: 'admin/ai-models', element: analyst(AiModelsAdminPage) },
      { path: 'admin/sender-phone-numbers', element: admin(SenderPhoneNumbersAdminPage) },
      { path: 'admin/reclassify', element: admin(ReclassifyAdminPage) },

      // 404
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
